import _ from "lodash";
import React, { cloneElement, useState, useEffect } from "react";
import classes from "./Header.scss";
import AssessmentIcon from "UIComponents/AssessmentIcon";
import I18nHOC from "UIComponents/I18nHOC";
import { connect } from "react-redux";
import { compose } from "lib/apolloClient";
import withDerivedProps from "lib/withDerivedProps";
import ToddleIcon from "SvgComponents/ToddleIcon";
import AssignmentEvaluationAICallout from "AppComponents/AssignmentEvaluationAICallout";
import {
  IconButton,
  Button,
  Tooltip,
  PageHeader,
  Badge,
} from "@toddle-edu/ds-web";
import {
  StarsTwoFilled,
  EyeOutlined,
  PrintOutlined,
  LockOpenOutlined,
  LockFilled,
  ChatOutlined,
} from "@toddle-edu/ds-icons";
import classnames from "classnames";
import {
  getAssessmentQuestionsQueryVariables,
  isWorksheetRevampV3,
} from "Worksheet/modules/WorksheetUtils";
import {
  ASSESSMENT,
  PLANNER,
  CREATE_WORKSHEET_FROM_TODDLE_LIBRARY,
  IMPORT_TODDLE_LIBRARY_WORKSHEET_IN_CLASS,
  ASSESSMENT_TYPE_AI_TUTOR,
  ASSESSMENT_TYPE_WORKSHEET,
  ASSIGNMENT_TYPE_QUICK_TASK,
  TASK_TYPE_AI_TUTOR,
  TASK_TYPE_WORKSHEET,
  TASK_TYPE_QUICK_TASK,
} from "Constants/stringConstants";
import { IMPORT_WORKSHEET_FROM_TODDLE_LIBRARY } from "Login/routes/ToddleLibrary/modules/Constants";
import { isAIModuleMarketingEnabled } from "Platform/modules/PlatformUtils";
import { AI_MODULES_FOR_MARKETING } from "ChildrenPage/components/AIModuleMarketingModal/AIModuleMarketingUtils";
import AIStarButton from "UIComponents/AIStarButton";
import { useDispatch } from "Hooks/useDispatch";
import {
  setAiModuleMarketingKey,
  getCurrentAssessmentTypes,
} from "Platform/modules/PlatformModule";
import WorksheetDebugModeInfo from "Worksheet/components/WorksheetDebugModeInfo";
import { getIsAIGradingEnabled } from "ClassRoom/modules/utils";
import { updateWorksheetAIPanel } from "Worksheet/modules/WorksheetModules";
import { updateLockedState } from "ClassRoom/modules/ClassRoomModule";
import {
  getIsWorksheetsDataLoaded,
  getIsWorksheetsDataLoadedV2,
} from "Worksheet/modules/WorksheetEnhancers";
import NewProductUpdatesCallout from "UIComponents/NewProductUpdatesCallout";
import { getSyncStatusCompData } from "LearningCourse/routes/CourseFlow/modules/Utils";
import {
  getIsSyncAndLockAllowed,
  getIsBlueprintSyncHistoryV2Enabled,
} from "LearningCourse/modules/LearningCourseUtils";
import AITutorChatResetButton from "AppComponents/AIComponents/AITutorChatResetButton";
import AITutorSegmentControl from "AppComponents/AIComponents/AITutorSegmentControl";
import {
  getIsAITutorModalitiesEnabled,
  getIsAssessmentTypeConfigurationEnabled,
} from "Utils/ACLStoreUtils";

const styles = {
  containerStyle: {
    padding: "0px",
  },
  rightContainerStyle: {
    gap: "8px",
  },
};

const COMMENT_BADGE_OFFSET = [8, 12];

const AI_GRADING_VIDEO_URL =
  "https://iframe.dacast.com/vod/ea71159a-bfba-7a06-500f-93b65dfdcdf5/cc2b2dd8-c0cd-4dca-845e-23d798ff692d";

const WORKSHEET_AI_VIDEO_DEMO =
  "https://iframe.dacast.com/vod/ea71159a-bfba-7a06-500f-93b65dfdcdf5/11f1e72e-bcc5-4adc-8cb9-9a13c6b6aae3";

const AI_TUTOR_LEARNING_TOOLS_VIDEO_URL =
  "https://iframe.dacast.com/vod/ea71159a-bfba-7a06-500f-93b65dfdcdf5/beb7ef78-fa94-4a0b-bbc1-340685c04848";

const Header = React.memo(props => {
  const {
    onClickBack,
    t,
    mode,
    createHeader,
    showImageTitleInHeader,
    assignmentDetails,
    editHeaderText,
    isLastStep,
    isWorksheetQuestionsStep,
    toggleWorksheetPreviewModal,
    setShowAssistantRightPane,
    isLessonPlanningEnabled,
    assessmentType,
    contentType,
    toggleDownloadWorksheetModal,
    isToddleLibrary,
    isWorksheetDataLoaded,
    creationMode,
    isPSOW,
    children, // Footer component with actions like assign, save as draft, etc.
    isGeneratingAIQuestions,
    isWorksheetCreationStep,
    testId,
    assessmentId,
    academicYearId,
    updateWorksheetAIPanel,
    showAssessmentLockIcon,
    isLocked: isAssessmentLocked,
    hasConflict,
    syncStatus,
    updateLockedState,
    getCurrentAssessmentTypes,
    questionCount,
    totalMarks,
    dynamicAssessmentIcon,
    taskTypeGroup,
    isGeneratingAIResponse,
    showCommentIcon,
    onCommentClick,
    isCommentActive,
    totalCommentCount = 0,
    unreadCommentCount = 0,
  } = props;

  const isWorksheet =
    taskTypeGroup === TASK_TYPE_WORKSHEET ||
    assessmentType === ASSESSMENT_TYPE_WORKSHEET;
  const isAITutor =
    taskTypeGroup === TASK_TYPE_AI_TUTOR ||
    assessmentType === ASSESSMENT_TYPE_AI_TUTOR;
  const dispatch = useDispatch();
  const [isLocked, setIsLocked] = useState(isAssessmentLocked);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const assessmentTypes = getCurrentAssessmentTypes();
  const { localeLabel, label: nonLocalisedLabel } =
    _.get(assessmentTypes, assessmentType) || {};
  const assessmentTypeLabel = t(localeLabel) || nonLocalisedLabel;

  const showAIIcon =
    !isToddleLibrary &&
    !isPSOW &&
    ((isLastStep && isLessonPlanningEnabled) ||
      isWorksheetQuestionsStep ||
      (isWorksheet && isWorksheetRevampV3()));

  const showMarketingAIIcon =
    !isToddleLibrary &&
    !isPSOW &&
    !isWorksheetQuestionsStep &&
    !isLessonPlanningEnabled &&
    (!_.includes(
      [
        ASSESSMENT_TYPE_AI_TUTOR,
        ASSESSMENT_TYPE_WORKSHEET,
        ASSIGNMENT_TYPE_QUICK_TASK,
      ],
      assessmentType
    ) ||
      !_.includes(
        [TASK_TYPE_AI_TUTOR, TASK_TYPE_WORKSHEET, TASK_TYPE_QUICK_TASK],
        taskTypeGroup
      )) &&
    dispatch(
      isAIModuleMarketingEnabled(AI_MODULES_FOR_MARKETING.LESSON_PLANNING)
    );

  const onAIButtonClick = () => {
    if (isWorksheet && isWorksheetRevampV3()) {
      updateWorksheetAIPanel({ isOpen: true });
      return;
    }
    setShowAssistantRightPane(prevState => !prevState);
  };

  const onAIMarketingClick = () => {
    dispatch(setAiModuleMarketingKey(AI_MODULES_FOR_MARKETING.LESSON_PLANNING));
  };

  const isFromToddleLibrary = _.includes(
    [
      CREATE_WORKSHEET_FROM_TODDLE_LIBRARY,
      IMPORT_TODDLE_LIBRARY_WORKSHEET_IN_CLASS,
      IMPORT_WORKSHEET_FROM_TODDLE_LIBRARY,
    ],
    creationMode
  );

  const previewButtonProps = {
    dsVersion: "2.0",
    size: "medium",
    variant: "neutral",
    type: "outlined",
    _legacy_variant: "outlined-subtle",
    _legacy_size: "medium",
    onClick: toggleWorksheetPreviewModal,
    icon: <EyeOutlined variant="subtle" />,
    disabled: isGeneratingAIQuestions,
  };

  const title = showImageTitleInHeader
    ? _.get(assignmentDetails, "assignmentTitle", "")
    : mode == "create"
    ? createHeader
    : editHeaderText
    ? editHeaderText
    : t(`common:edit_with_label`, {
        label: t(`common:lowercase`, {
          text: t(`common:classwork`),
        }),
      });

  const icon = showImageTitleInHeader ? (
    <div
      className={classes.assignmentImage}
      style={{
        backgroundImage: `url(${assignmentDetails.assignmentImage})`,
      }}
    ></div>
  ) : dynamicAssessmentIcon ? (
    dynamicAssessmentIcon
  ) : assessmentType ? (
    <AssessmentIcon
      assessmentType={assessmentType}
      contentType={contentType}
      iconSize="medium"
    />
  ) : (
    <ToddleIcon width={32} height={32} />
  );

  const handleAssessmentLock = () => {
    updateLockedState({
      assessmentId,
      assessmentType,
      isLocked: !isLocked,
      hasConflict,
      t,
    });
    setIsLocked(!isLocked);
  };

  const isAssessmentTypeConfigurationEnabled = getIsAssessmentTypeConfigurationEnabled();

  const showAIGradingCallout =
    getIsAIGradingEnabled() &&
    !_.includes(
      [
        ASSESSMENT_TYPE_AI_TUTOR,
        ASSESSMENT_TYPE_WORKSHEET,
        ASSIGNMENT_TYPE_QUICK_TASK,
      ],
      assessmentType
    ) &&
    (!isAssessmentTypeConfigurationEnabled ||
      !_.includes(
        [TASK_TYPE_AI_TUTOR, TASK_TYPE_WORKSHEET, TASK_TYPE_QUICK_TASK],
        taskTypeGroup
      )) &&
    !isToddleLibrary &&
    !isPSOW &&
    isLastStep;

  const questionDetailsClasses = classnames({
    [classes.questionDetails]: true,
    "text-label": true,
  });

  const showDisabled =
    !getIsBlueprintSyncHistoryV2Enabled() && hasConflict && !isLocked;

  const { icon: statusIcon, tooltip } = getSyncStatusCompData({
    syncStatus,
    size: "xx-small",
    t,
  });

  const isSyncAndLockAllowed = getIsSyncAndLockAllowed();

  const isAITutorModalitiesEnabled =
    isAITutor && getIsAITutorModalitiesEnabled();

  const isSmallScreen = windowWidth < 1400;

  const renderComment = () => {
    if (totalCommentCount === 0) {
      return (
        <IconButton
          icon={<ChatOutlined />}
          dsVersion={"2.0"}
          size={"medium"}
          variant={"neutral"}
          type={"outlined"}
          isActivated={isCommentActive}
          onClick={onCommentClick}
          testId={`${testId}-header-chat`}
          aria-label={t("common:comments")}
        />
      );
    }

    const button = (
      <Button
        variant={"neutral"}
        type={"outlined"}
        size={"medium"}
        dsVersion={"2.0"}
        icon={<ChatOutlined />}
        isActivated={isCommentActive}
        onClick={onCommentClick}
        testId={`${testId}-header-chat`}
      >
        {totalCommentCount}
      </Button>
    );

    if (unreadCommentCount > 0) {
      return (
        <Badge
          dsVersion={"2.0"}
          placement="topLeft"
          type="dot"
          offset={COMMENT_BADGE_OFFSET}
        >
          {button}
        </Badge>
      );
    }
    return button;
  };

  return (
    <PageHeader dsVersion={"2.0"}>
      <PageHeader.Left onBackClick={onClickBack} title={title} icon={icon} />
      {isAITutorModalitiesEnabled ? (
        <PageHeader.Middle>
          <AITutorSegmentControl assessmentId={assessmentId} />
        </PageHeader.Middle>
      ) : null}
      <PageHeader.Right
        width={
          isAITutorModalitiesEnabled && !isSmallScreen ? "500px" : undefined
        }
      >
        {isAITutorModalitiesEnabled ? (
          <NewProductUpdatesCallout
            uid="AI_TUTOR"
            videoUrl={AI_TUTOR_LEARNING_TOOLS_VIDEO_URL}
            title={t("ai:call_out_heading")}
            description={t("ai:call_out_description")}
            customDescriptionClass="text-body"
          />
        ) : null}
        <AITutorChatResetButton
          assessmentId={assessmentId}
          isGeneratingAIResponse={isGeneratingAIResponse}
        />

        {isWorksheetCreationStep && (
          <div className={questionDetailsClasses}>
            {t("common:question_number", { count: questionCount })} •{" "}
            {t("toddleLibrary:marks_with_count", { count: totalMarks })}
          </div>
        )}

        {showAssessmentLockIcon ? (
          <div className={classes.lockIconContainer}>
            {!_.isEmpty(statusIcon) && (
              <Tooltip tooltip={tooltip} placement="left" showArrow={true}>
                {statusIcon}
              </Tooltip>
            )}
            <Tooltip
              dsVersion="2.0"
              tooltip={t(
                !isSyncAndLockAllowed
                  ? isLocked
                    ? "course:unlock_item_with_label_read_only"
                    : "course:lock_item_with_label_read_only"
                  : showDisabled
                  ? "course:cannot_lock_in_conflict"
                  : isLocked
                  ? "course:unlock_item_with_label"
                  : "course:lock_item_with_label",
                {
                  label: assessmentTypeLabel,
                }
              )}
              placement="left"
              showArrow={true}
            >
              <IconButton
                dsVersion="2.0"
                variant="neutral"
                type="outlined"
                disabled={showDisabled}
                icon={isLocked ? <LockFilled /> : <LockOpenOutlined />}
                onClick={isSyncAndLockAllowed ? handleAssessmentLock : _.noop}
              />
            </Tooltip>
          </div>
        ) : null}
        {showAIGradingCallout ? (
          <AssignmentEvaluationAICallout
            title={t("ai:discover_the_ai_grading_assistant")}
            message={t("ai:discover_the_ai_grading_assistant_message")}
            videoUrl={AI_GRADING_VIDEO_URL}
          />
        ) : null}
        {showAIIcon ? (
          <div
            role={"button"}
            aria-label={"AI chat button"}
            className={classes.assistantAvatar}
            onClick={onAIButtonClick}
            tabIndex={0}
            onKeyDown={e =>
              (e.key === "Enter" || e.key === " ") && onAIButtonClick()
            }
          >
            <StarsTwoFilled variant={"link"} />
          </div>
        ) : null}
        {showMarketingAIIcon ? (
          <AIStarButton onClick={onAIMarketingClick} />
        ) : null}
        {showCommentIcon ? renderComment() : null}
        {isWorksheetQuestionsStep || isWorksheetCreationStep ? (
          <div className={classes.buttonContainer}>
            <WorksheetDebugModeInfo
              assessmentId={assessmentId}
              isToddleLibrary={isToddleLibrary}
              academicYearId={academicYearId}
            />
            <div className={classes.buttonContainer}>
              {isWorksheetCreationStep ? (
                <>
                  {!isFromToddleLibrary && (
                    <NewProductUpdatesCallout
                      uid="WORKSHEET"
                      videoUrl={WORKSHEET_AI_VIDEO_DEMO}
                      title={t("worksheet:call_out_title")}
                      description={t("worksheet:call_out_subtitle")}
                    />
                  )}
                  {!isFromToddleLibrary ? (
                    <Tooltip
                      tooltip={t("common:print_with_label", {
                        label: t("common:worksheet"),
                      })}
                    >
                      <IconButton
                        dsVersion={"2.0"}
                        size="medium"
                        variant="neutral"
                        type="outlined"
                        _legacy_variant={"outlined-subtle"}
                        _legacy_size={"medium"}
                        icon={<PrintOutlined variant="subtle" />}
                        onClick={toggleDownloadWorksheetModal}
                        testId={`${testId}-header-worksheet-print`}
                        disabled={
                          !isWorksheetDataLoaded || isGeneratingAIQuestions
                        }
                        aria-label={t("common:print_with_label", {
                          label: t("common:worksheet"),
                        })}
                      />
                    </Tooltip>
                  ) : null}
                  <Tooltip tooltip={t("worksheet:student_preview")}>
                    <IconButton
                      {...previewButtonProps}
                      testId={`${testId}-header-worksheet-studentPreviewIcon`}
                      aria-label={t("worksheet:student_preview")}
                    />
                  </Tooltip>
                </>
              ) : (
                <>
                  <Button
                    {...previewButtonProps}
                    testId={`${testId}-header-worksheet-studentPreview`}
                  >
                    {t("worksheet:student_preview")}
                  </Button>
                  {!isFromToddleLibrary ? (
                    <Tooltip
                      tooltip={t("common:print_with_label", {
                        label: t("common:worksheet"),
                      })}
                    >
                      <IconButton
                        dsVersion={"2.0"}
                        size="medium"
                        variant="neutral"
                        type="outlined"
                        _legacy_variant={"outlined-subtle"}
                        _legacy_size={"medium"}
                        icon={<PrintOutlined variant="subtle" />}
                        onClick={toggleDownloadWorksheetModal}
                        testId={`${testId}-header-worksheet-print`}
                        disabled={
                          !isWorksheetDataLoaded || isGeneratingAIQuestions
                        }
                        aria-label={t("common:print_with_label", {
                          label: t("common:worksheet"),
                        })}
                      />
                    </Tooltip>
                  ) : null}
                </>
              )}
            </div>
          </div>
        ) : null}
        {children
          ? cloneElement(children, {
              showCancelButton: false,
              containerStyle: styles.containerStyle,
              rightContainerStyle: styles.rightContainerStyle,
            })
          : null}
      </PageHeader.Right>
    </PageHeader>
  );
});

Header.displayName = "Header";

const mapStateToProps = state => {
  const isGeneratingAIQuestions = _.get(
    state,
    "worksheet.generatingAIQuestions",
    false
  );

  return {
    isGeneratingAIQuestions,
  };
};

const mapActionCreators = {
  updateLockedState,
  updateWorksheetAIPanel,
  getCurrentAssessmentTypes,
};

const getDerivedProps = ownProps => {
  const { parentId } = ownProps;
  const assessmentQuestionsQueryVariables = getAssessmentQuestionsQueryVariables(
    {
      parentId,
      parentType: ASSESSMENT,
      portalType: PLANNER,
    }
  );

  const assessmentQuestionsQueryVariablesV2 = {
    id: parentId,
    type: ASSESSMENT,
    portalType: PLANNER,
  };
  return {
    assessmentQuestionsQueryVariables,
    assessmentQuestionsQueryVariablesV2,
  };
};

export default compose(
  I18nHOC({ resource: ["common", "teacherHomePage", "classRoom"] }),
  connect(mapStateToProps, mapActionCreators),
  withDerivedProps(getDerivedProps),
  getIsWorksheetsDataLoaded(), //WORKSHEET_RESTRUCTURING: Remove this when queriesV2 is enabled
  getIsWorksheetsDataLoadedV2()
)(Header);
